import { useEffect, useRef, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { useSearchParams } from "react-router-dom";

import { IDoctorSpecialty } from "../../../providers/doctorsData/types";
import { useFilters } from "../../../providers/filters";
import { IDropDown } from "./types";

export const DropDownMenu = ({
  itensList,
  label,
  icon,
  value,
  setValue,
  theme,
  disabled = false
}: IDropDown) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterList, setFilterList] = useState(itensList);
  const [selectedItem, setSelectedItem] = useState<number>(-1); // -1 = nenhum/"limpar"
  const [inputText, setInputText] = useState(""); // controlar o texto do input
  const inputRef = useRef<HTMLInputElement>(null);
  const clearRef = useRef<HTMLAnchorElement | null>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]); // refs dos itens filtrados
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const focusByKeyboard = useRef(false);

  const displayText = value || label;

  const reset = () => {
    setFilterList(itensList);
    setSelectedItem(-1);
    setInputText(""); // limpar o input também
  };

  const onFilter = (term: string) => {
    if (!term) {
      reset();
      return;
    }
    const existentItems: IDoctorSpecialty[] = itensList.filter((elm) =>
      elm.key.name.toLowerCase().includes(term.toLowerCase())
    );
    setFilterList(existentItems);
    // Ao filtrar, selecionar o primeiro resultado automaticamente
    setSelectedItem(existentItems.length > 0 ? 0 : -1);
  };

  const handleItemSelect = (itemLabel: string | null, index: number) => {
    // index -1 = "limpar"
    if (index === -1) {
      setSelectedItem(-1);
      setValue("");
      setInputText(""); // limpar o input
      setIsOpen(false); // fechar dropdown
      return;
    }
    setSelectedItem(index);
    setValue(itemLabel || "");
    setInputText(""); // limpar o input após seleção
    setIsOpen(false); // fechar dropdown após seleção
  };

  // Foco & seleção via teclado quando o foco NÃO está no input
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!filterList.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusByKeyboard.current = true;
      if (selectedItem === -1) {
        // Do "limpar" vai para o primeiro item
        setSelectedItem(0);
      } else if (selectedItem === filterList.length - 1) {
        // Do último item volta para "limpar"
        setSelectedItem(-1);
      } else {
        // Próximo item
        setSelectedItem(selectedItem + 1);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusByKeyboard.current = true;
      if (selectedItem === -1) {
        // Do "limpar" vai para o último item
        setSelectedItem(filterList.length - 1);
      } else if (selectedItem === 0) {
        // Do primeiro item volta para "limpar"
        setSelectedItem(-1);
      } else {
        // Item anterior
        setSelectedItem(selectedItem - 1);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedItem >= 0) {
        handleItemSelect(filterList[selectedItem].key.name, selectedItem);
      } else {
        handleItemSelect(null, -1);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      reset();
      inputRef.current?.blur();
    }
  };

  // Setas dentro do input: mover seleção + sair do input
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filterList.length) return;

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      focusByKeyboard.current = true;

      // Usar a mesma lógica do handleMenuKeyDown para consistência
      if (e.key === "ArrowDown") {
        if (selectedItem === -1) {
          setSelectedItem(0);
        } else if (selectedItem === filterList.length - 1) {
          setSelectedItem(-1);
        } else {
          setSelectedItem(selectedItem + 1);
        }
      } else { // ArrowUp
        if (selectedItem === -1) {
          setSelectedItem(filterList.length - 1);
        } else if (selectedItem === 0) {
          setSelectedItem(-1);
        } else {
          setSelectedItem(selectedItem - 1);
        }
      }

      inputRef.current?.blur(); // sair do input
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedItem >= 0) {
        handleItemSelect(filterList[selectedItem].key.name, selectedItem);
      } else {
        handleItemSelect(null, -1);
      }
    }
  };

  // Ao abrir o dropdown, centralizar o item selecionado
  useEffect(() => {
    if (!isOpen) return;

    // Aguardar um pouco para o dropdown renderizar completamente
    setTimeout(() => {
      const target = selectedItem === -1
        ? clearRef.current
        : itemRefs.current[selectedItem];

      const scrollContainer = scrollContainerRef.current;

      if (target && scrollContainer) {
        // Calcular posições
        const containerRect = scrollContainer.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        // Posição do item relativa ao container
        const targetRelativeTop = target.offsetTop;
        const targetHeight = targetRect.height;
        const containerHeight = containerRect.height;

        // Calcular o scroll necessário para centralizar o item
        const scrollTop = targetRelativeTop - (containerHeight / 2) + (targetHeight / 2);

        // Fazer o scroll suave
        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: 'auto'
        });
      }
    }, 200);
  }, [isOpen]); // executa apenas quando isOpen muda

  // Sincronizar input text quando value mudar externamente
  useEffect(() => {
    // Se value mudou externamente e não estamos digitando, limpar o input
    if (!isOpen) {
      setInputText("");
    }
  }, [value, isOpen]);

  // sincroniza a lista visível com as props sempre que itensList mudar
  useEffect(() => {
    setFilterList(itensList ?? []);
  }, [itensList]);

  // mantém o índice selecionado coerente com o value atual e a lista exibida
  useEffect(() => {
    const idx = value
      ? filterList.findIndex((e) => e.key.name.toLowerCase() === value.toLowerCase())
      : -1;
    setSelectedItem(idx);
  }, [value, filterList]);

  // Se a lista filtrada mudou e a seleção ficou inválida, ajustar
  useEffect(() => {
    if (selectedItem >= filterList.length) {
      setSelectedItem(filterList.length ? filterList.length - 1 : -1);
    }
  }, [filterList, selectedItem]);

  // Ao mudar selectedItem com o menu aberto, focar o item correspondente
  // garante foco ao mudar seleção APENAS quando navegando por teclado
  useEffect(() => {
    if (!isOpen) return;
    if (!focusByKeyboard.current) return; // não tira foco do input ao digitar

    const target =
      selectedItem === -1 ? clearRef.current : itemRefs.current[selectedItem];
    target?.focus?.();
    focusByKeyboard.current = false; // resetar depois de usar
  }, [selectedItem, isOpen]);

  // Atualizar cache de refs a cada render da lista
  itemRefs.current = [];

  return (
    <Dropdown
      show={isOpen}
      onToggle={(open) => {
        setIsOpen(!!open);
        if (open) {
          // Ao abrir, limpar o input e focar para digitar imediatamente
          setInputText("");
          setTimeout(() => inputRef.current?.focus(), 150);
        }
      }}
    >
      <>
        <Dropdown.Toggle
          disabled={filterList.length <= 0}
          variant={
            theme === "light"
              ? "btn text-decoration-none btn-outline-light"
              : "btn text-decoration-none btn-group btn-outline-secondary"
          }
        >
          <i className={icon}></i>{" "}
          <span>{displayText}</span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="pt-0" onKeyDown={handleMenuKeyDown}>
          <div className="border-bottom" style={{ top: 0 }}>
            <div className="position-relative p-1">
              <input
                ref={inputRef}
                value={inputText}
                onChange={(e) => {
                  // Atualizar o estado do input e filtrar
                  setInputText(e.target.value);
                  onFilter(e.target.value);
                }}
                onKeyDown={handleInputKeyDown}
                className="form-control form-control-sm"
                placeholder="Buscar"
              />
            </div>
          </div>
          <div ref={scrollContainerRef} className="scroll-dropdown">
            {/* Item "limpar" (label) */}
            <Dropdown.Item
              ref={clearRef}
              onClick={() => handleItemSelect(null, -1)}
              active={selectedItem === -1}
            >
              {label}
            </Dropdown.Item>

            {/* Itens filtrados */}
            {filterList.map((elm, i) => (
              <Dropdown.Item
                key={`${elm.key.name}-${i}`}
                ref={(el: HTMLAnchorElement) => (itemRefs.current[i] = el)}
                onClick={() => handleItemSelect(elm.key.name, i)}
                active={selectedItem === i}
              >
                {elm.key.name} <span className="opacity-50">({elm.value})</span>
              </Dropdown.Item>
            ))}
          </div>

        </Dropdown.Menu>
      </>
    </Dropdown>
  );
};